import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { RoleState } from "../../state/identity.tsx";
import { WriteButton } from "./WriteControls.tsx";
import {
  RegisterTelemetryKeyForm,
  RevokeDialog,
  ApproveAuditKeyDialog,
  ReverificationNote,
} from "./write-forms.tsx";
import {
  adminLinkSummary,
  auditKeyLevel,
  auditKeyStateLabel,
  formatCount,
  formatSeq,
  formatSkew,
  formatTime,
  livenessLabel,
  registrationLabel,
  resolveLiveness,
  shortId,
  sourceLevel,
  sourceStatusLabel,
  telemetryKeyLabel,
  telemetryKeyLevel,
  type AuditKeyPin,
  type Source,
  type SourceLivenessState,
  type TelemetryKey,
} from "./vocab.ts";

/**
 * The registered-sources list (task 11.14; design 12.2 Sources row, 12.4). A semantic DenseTable of
 * every source — status (preferring the live stream's liveness for freshness), last seq, drops,
 * skew, environment, service, admin link and jwks url — with each row EXPANDABLE to its telemetry
 * keys and audit-key pins.
 *
 * Liveness is RESOLVED per source: the live stream's `live`/`silent` state (passed in as
 * `streamLiveness`) overrides the REST snapshot's `liveness` when present (the stream is fresher),
 * falling back to REST — so a `never_seen` source (never in the live view) still reads as such.
 * Nothing a source reports is ever shown as the healthy green: a live source is neutral, a silent
 * one warns, a never-seen one is unknown (design 12.3).
 *
 * The drill-downs (design 12.3 "numbers drill down"): last seq and drops link to the events explorer
 * filtered to the source; skew links to the system health view. These use the shared `?source=`
 * query the other screens read.
 */
export function SourcesList(props: {
  role: RoleState;
  sources: readonly Source[];
  /** Per-source live/silent state from the stream, keyed by source id; overrides REST liveness. */
  streamLiveness: ReadonlyMap<string, "live" | "silent">;
}): ReactNode {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sort: problems first (revoked, then silent/never-seen), then by name — so the operator sees what
  // needs attention at the top without hiding anything.
  const sorted = [...props.sources].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));

  const columns: readonly Column<Source>[] = [
    {
      key: "expand",
      header: <span className="sr-only">Expand</span>,
      width: "sm",
      cell: (s) => (
        <button
          type="button"
          className="src-expand"
          aria-expanded={expanded.has(s.id)}
          aria-label={expanded.has(s.id) ? `Collapse ${s.name}` : `Expand ${s.name} to its keys and pins`}
          onClick={() => toggle(s.id)}
        >
          {expanded.has(s.id) ? "▾" : "▸"}
        </button>
      ),
    },
    {
      key: "name",
      header: "Source",
      isRowHeader: true,
      cell: (s) => <NameCell source={s} />,
    },
    { key: "service", header: "Service", width: "sm", cell: (s) => s.service },
    { key: "env", header: "Environment", width: "sm", cell: (s) => s.environment },
    {
      key: "status",
      header: "Status",
      cell: (s) => {
        const liveness = resolveLiveness(s.liveness, props.streamLiveness.get(s.id));
        return (
          <StatusBadge
            level={sourceLevel(s, liveness)}
            label={statusText(s.status, liveness)}
            size="sm"
            title={statusTitle(s, liveness)}
          />
        );
      },
    },
    {
      key: "lastseq",
      header: "Last seq",
      align: "right",
      measure: "exact",
      cell: (s) => <SeqCell source={s} />,
    },
    {
      key: "drops",
      header: "Drops",
      align: "right",
      measure: "exact",
      cell: (s) => <DropsCell source={s} />,
    },
    {
      key: "skew",
      header: "Clock skew",
      align: "right",
      cell: (s) => <SkewCell source={s} />,
    },
    {
      key: "pins",
      header: "Audit keys",
      cell: (s) => <PinSummary source={s} />,
    },
  ];

  return (
    <>
      <DenseTable
        caption="Registered sources with status, last sequence, drops, clock skew and audit-key pins. Expand a source for its keys and pins."
        columns={columns}
        rows={sorted}
        rowKey={(s) => s.id}
        stickyHeader
        emptyMessage="No sources are registered. Enroll one with the wizard above."
      />
      {sorted
        .filter((s) => expanded.has(s.id))
        .map((s) => (
          <SourceDetail key={s.id} role={props.role} source={s} />
        ))}
    </>
  );
}

/* ---- Name cell: the source name, its id and the licensed tag -------------------------------- */

/**
 * The `name` column cell: the source name, its short id and — for a license-registered source only
 * — a `Licensed` tag beside the name (design "Sources list" §4; Requirements 18.4, 18.6). The tag
 * conveys the licensed state by TEXT with a border, never colour alone (design 12.4): a plain
 * `<span>` carrying the visible word `Licensed` and a `title` with the scope, the instance count and
 * the registered time, so assistive tech announces the full phrase. An admin-registered source has
 * no `registration` member, so `registrationLabel` returns null and NO tag renders.
 */
function NameCell(props: { source: Source }): ReactNode {
  const label = registrationLabel(props.source);
  return (
    <span className="src-name">
      <span className="src-name__label">
        {props.source.name}
        {label !== null && (
          <span className="src-lic-tag" title={label.title}>
            {label.tag}
          </span>
        )}
      </span>
      <span className="src-name__id src-faint src-mono">{shortId(props.source.id)}</span>
    </span>
  );
}

/* ---- Drill-down cells ----------------------------------------------------------------------- */

function SeqCell(props: { source: Source }): ReactNode {
  const navigate = useNavigate();
  const text = formatSeq(props.source.last_seq);
  if (props.source.last_seq === null) return <span className="src-num src-faint">{text}</span>;
  return (
    <button
      type="button"
      className="src-link src-num"
      onClick={() => navigate(`/events?source=${encodeURIComponent(props.source.id)}`)}
      title="See this source's events"
    >
      {text}
    </button>
  );
}

function DropsCell(props: { source: Source }): ReactNode {
  const navigate = useNavigate();
  const drops = props.source.reported_drops;
  if (drops === 0) return <span className="src-num src-faint">0</span>;
  return (
    <button
      type="button"
      className="src-link src-num"
      data-warn="true"
      onClick={() => navigate(`/events?source=${encodeURIComponent(props.source.id)}`)}
      title="Reported drops. See this source's events"
    >
      {formatCount(drops)}
    </button>
  );
}

function SkewCell(props: { source: Source }): ReactNode {
  const navigate = useNavigate();
  const skew = props.source.clock_skew_ms;
  if (skew === null) return <span className="src-num src-faint">n/a</span>;
  const warn = Math.abs(skew) >= 5000;
  return (
    <button
      type="button"
      className="src-link src-num"
      data-warn={warn ? "true" : undefined}
      onClick={() => navigate("/system")}
      title="Measured clock skew. See control plane health"
    >
      {formatSkew(skew)}
    </button>
  );
}

/** A compact one-line summary of a source's audit-key pins for the list cell. */
function PinSummary(props: { source: Source }): ReactNode {
  const pins = props.source.audit_key_pins;
  if (props.source.audit_key_policy === "tofu") {
    return <StatusBadge level="warn" label="TOFU (demo)" size="sm" title="Trust on first use. Demo profile only (FR-11.5)" />;
  }
  if (pins.length === 0) {
    return <StatusBadge level="unknown" label="No pins" size="sm" title="No audit signing key has been observed or pinned yet" />;
  }
  const pinned = pins.filter((p) => p.state === "pinned").length;
  const observed = pins.filter((p) => p.state === "observed_unpinned").length;
  if (observed > 0) {
    return (
      <StatusBadge
        level="warn"
        label={`${observed} unpinned`}
        size="sm"
        title={`${observed} observed audit key(s) await approval; ${pinned} pinned`}
      />
    );
  }
  return <StatusBadge level="neutral" label={`${pinned} pinned`} size="sm" />;
}

/* ---- Expanded detail: keys + pins ----------------------------------------------------------- */

function SourceDetail(props: { role: RoleState; source: Source }): ReactNode {
  const [registering, setRegistering] = useState(false);
  const [revoking, setRevoking] = useState<TelemetryKey | "source" | null>(null);
  const [approving, setApproving] = useState<AuditKeyPin | null>(null);

  const s = props.source;

  return (
    <section className="src-detail" aria-label={`Keys and pins for ${s.name}`}>
      <div className="src-detail__grid">
        <dl className="src-detail__meta">
          <div>
            <dt>Admin link</dt>
            <dd>{adminLinkSummary(s.admin_link)}</dd>
          </div>
          <div>
            <dt>JWKS URL</dt>
            <dd className="src-mono">{s.jwks_url ?? "n/a"}</dd>
          </div>
          <div>
            <dt>Audit-key policy</dt>
            <dd>{s.audit_key_policy === "tofu" ? "Trust on first use (demo)" : "Pinned keys"}</dd>
          </div>
          <div>
            <dt>Registered</dt>
            <dd>{formatTime(s.created_at)}</dd>
          </div>
        </dl>
      </div>

      {/* ---- Telemetry keys ---- */}
      <div className="src-detail__region">
        <div className="src-detail__head">
          <h3 className="src-detail__title">Telemetry keys</h3>
          <div className="src-row-actions">
            <WriteButton
              role={props.role}
              onClick={() => setRegistering(true)}
              variant="primary"
              disabled={s.status === "revoked"}
              title={s.status === "revoked" ? "This source is revoked." : undefined}
            >
              Register public key
            </WriteButton>
            {s.status !== "revoked" && (
              <WriteButton role={props.role} onClick={() => setRevoking("source")} variant="danger">
                Revoke source
              </WriteButton>
            )}
          </div>
        </div>
        <KeysTable role={props.role} source={s} onRevokeKey={(k) => setRevoking(k)} />
      </div>

      {/* ---- Audit-key pins ---- */}
      <div className="src-detail__region">
        <h3 className="src-detail__title">Audit signing key pins</h3>
        <PinsTable role={props.role} source={s} onApprove={(p) => setApproving(p)} />
      </div>

      {registering && <RegisterTelemetryKeyForm role={props.role} source={s} onClose={() => setRegistering(false)} />}
      {revoking !== null && (
        <RevokeDialog
          role={props.role}
          source={s}
          telemetryKey={revoking === "source" ? undefined : revoking}
          onClose={() => setRevoking(null)}
        />
      )}
      {approving !== null && (
        <ApproveAuditKeyDialog role={props.role} source={s} pin={approving} onClose={() => setApproving(null)} />
      )}
    </section>
  );
}

function KeysTable(props: {
  role: RoleState;
  source: Source;
  onRevokeKey: (key: TelemetryKey) => void;
}): ReactNode {
  const columns: readonly Column<TelemetryKey>[] = [
    { key: "kid", header: "Key id", isRowHeader: true, cell: (k) => <span className="src-mono">{k.kid}</span> },
    {
      key: "state",
      header: "State",
      cell: (k) => <StatusBadge level={telemetryKeyLevel(k.status)} label={telemetryKeyLabel(k.status)} size="sm" />,
    },
    { key: "kty", header: "Type", width: "sm", cell: (k) => k.public_jwk.kty },
    { key: "from", header: "Not before", cell: (k) => formatTime(k.not_before) },
    { key: "until", header: "Not after", cell: (k) => formatTime(k.not_after) },
    {
      key: "actions",
      header: "Actions",
      cell: (k) =>
        k.status === "revoked" ? (
          <span className="src-faint">n/a</span>
        ) : (
          <WriteButton role={props.role} onClick={() => props.onRevokeKey(k)} variant="danger">
            Revoke key
          </WriteButton>
        ),
    },
  ];
  return (
    <DenseTable
      caption={`Telemetry keys for ${props.source.name}. Keys are public; the control plane never holds a private key.`}
      captionHidden
      columns={columns}
      rows={props.source.telemetry_keys}
      rowKey={(k) => k.kid}
      emptyMessage="No telemetry keys registered yet. Register the component's public JWK to accept its batches."
    />
  );
}

function PinsTable(props: {
  role: RoleState;
  source: Source;
  onApprove: (pin: AuditKeyPin) => void;
}): ReactNode {
  const columns: readonly Column<AuditKeyPin>[] = [
    { key: "kid", header: "Key id", isRowHeader: true, cell: (p) => <span className="src-mono">{p.kid}</span> },
    {
      key: "state",
      header: "State",
      cell: (p) => <StatusBadge level={auditKeyLevel(p.state)} label={auditKeyStateLabel(p.state)} size="sm" />,
    },
    { key: "thumb", header: "Thumbprint", cell: (p) => <span className="src-mono">{shortId(p.jwk_thumbprint, 12, 6)}</span> },
    { key: "seen", header: "First seen", cell: (p) => formatTime(p.first_seen) },
    {
      key: "reverify",
      header: "Re-verification",
      cell: (p) => (p.reverification === null ? <span className="src-faint">n/a</span> : <ReverificationNote reverification={p.reverification} />),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (p) =>
        p.state === "observed_unpinned" ? (
          <WriteButton role={props.role} onClick={() => props.onApprove(p)} variant="primary">
            Pin &amp; re-verify
          </WriteButton>
        ) : (
          <span className="src-faint">n/a</span>
        ),
    },
  ];
  return (
    <DenseTable
      caption={`Audit signing key pins for ${props.source.name}. Approving an observed key re-verifies its stored batches.`}
      captionHidden
      columns={columns}
      rows={props.source.audit_key_pins}
      rowKey={(p) => p.id}
      emptyMessage={
        props.source.audit_key_policy === "tofu"
          ? "This source uses trust-on-first-use (demo). No pins are required."
          : "No audit signing keys observed or pinned yet. A key appears here once the source signs an audit batch."
      }
    />
  );
}

/* ---- helpers -------------------------------------------------------------------------------- */

/** Sort rank: revoked first (needs attention), then silent/never-seen, then live/active. */
function rank(s: Source): number {
  if (s.status === "revoked") return 0;
  if (s.liveness === "silent") return 1;
  if (s.liveness === "never_seen") return 2;
  return 3;
}

/** The combined status + liveness text for the badge. */
function statusText(status: Source["status"], liveness: SourceLivenessState): string {
  if (status !== "active") return sourceStatusLabel(status);
  return livenessLabel(liveness);
}

/** A fuller accessible name for the status badge. */
function statusTitle(source: Source, liveness: SourceLivenessState): string {
  const bits = [`Status: ${sourceStatusLabel(source.status)}`, `liveness: ${livenessLabel(liveness)}`];
  if (source.last_heartbeat_at !== null) bits.push(`last heartbeat ${formatTime(source.last_heartbeat_at)}`);
  return bits.join(" · ");
}
